import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { DocumentType } from "../types/Types";

interface WorkOrderDocumentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (documentType: DocumentType) => void;
  currentValue?: string;
}

interface SpreadsheetWorkOrderDocumentInputProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const WorkOrderDocumentSearchModal: React.FC<WorkOrderDocumentSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
}) => {
  const { t } = useTranslation();
  const { documentTypes, loading } = useDocumentTypesContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const workOrderDocuments = useMemo(
    () => documentTypes.filter((doc) => doc.category === "work-order"),
    [documentTypes]
  );

  const filteredDocuments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return workOrderDocuments;

    return workOrderDocuments.filter((doc) =>
      doc.code.toLowerCase().includes(term) || doc.name.toLowerCase().includes(term)
    );
  }, [searchTerm, workOrderDocuments]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm("");
    setSelectedIndex(0);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  const handleSelect = (documentType: DocumentType) => {
    onSelect(documentType);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredDocuments.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredDocuments[selectedIndex]) {
          handleSelect(filteredDocuments[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[70vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              {t("forms.selectWorkOrderDocument", "Работен налог")}
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              {t("search.keyboardInstructions", "Use arrow keys to navigate, Enter to select, Escape to close")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("documentTypeConfig.searchPlaceholder", "Search by code or name...") as string}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg"
              autoFocus
            />
          </div>

          <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl" style={{ minHeight: "250px" }}>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-600">
                {t("common.loading", "Loading...")}
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left">
                    <th className="py-3 px-4 font-semibold text-gray-700 border-b w-32">
                      {t("tableHeaders.code", "Код")}
                    </th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-b">
                      {t("tableHeaders.name", "Назив")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc, index) => {
                    const isHighlighted = selectedIndex === index;
                    return (
                      <tr
                        key={doc._id}
                        onClick={() => handleSelect(doc)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`border-b border-gray-100 cursor-pointer transition-colors duration-150 ${
                          isHighlighted
                            ? "bg-yellow-100 border-yellow-300 ring-2 ring-yellow-400 ring-opacity-50 shadow-md"
                            : "hover:bg-gray-50"
                        } ${currentValue === doc._id ? "font-semibold" : ""}`}
                      >
                        <td className={`py-3 px-4 font-medium ${isHighlighted ? "text-yellow-900" : "text-gray-900"}`}>
                          {doc.code}
                        </td>
                        <td className={`py-3 px-4 ${isHighlighted ? "text-yellow-800" : "text-gray-700"}`}>
                          {doc.name}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredDocuments.length === 0 && (
                    <tr>
                      <td colSpan={2} className="text-center text-gray-500 py-12">
                        {t("forms.noWorkOrderDocumentsFound", "No work order documents found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SpreadsheetWorkOrderDocumentInput = forwardRef<HTMLInputElement, SpreadsheetWorkOrderDocumentInputProps>(
  ({ value, onChange, disabled = false }, ref) => {
    const { documentTypes } = useDocumentTypesContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const selectedDocument = useMemo(
      () => documentTypes.find((doc) => doc._id === value && doc.category === "work-order"),
      [documentTypes, value]
    );

    const openModal = () => {
      if (!disabled) setIsModalOpen(true);
    };

    return (
      <div className="w-full h-6">
        <div className="relative h-6">
          {/* Read-only (derived) value: no magnifier, just the code on a soft grey cell. */}
          {!disabled && <button
            type="button"
            className="absolute left-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              openModal();
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Search (F4)"
            disabled={disabled}
          >
            <Search size={14} />
          </button>}

          <input
            ref={inputRef}
            type="text"
            value={selectedDocument?.code || ""}
            onClick={openModal}
            onFocus={openModal}
            onKeyDown={(e) => {
              if (e.key === "F4" || e.key === "Enter") {
                e.preventDefault();
                openModal();
              }
              if (e.key === "Backspace" || e.key === "Delete") {
                onChange("");
              }
            }}
            readOnly
            disabled={disabled}
            className={`w-full h-6 ${disabled ? "px-1 text-center text-[11px] font-medium text-gray-600 bg-gray-50 cursor-default" : "pl-5 pr-1 bg-transparent"} border-0 outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50`}
            autoComplete="off"
          />
        </div>

        <WorkOrderDocumentSearchModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelect={(documentType) => onChange(documentType._id)}
          currentValue={value}
        />
      </div>
    );
  }
);

export default SpreadsheetWorkOrderDocumentInput;
